import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, CheckCircle2, ChevronDown, FileUp, ImagePlus, Loader2, RotateCcw, ShieldCheck, X } from 'lucide-react';
import { COUNTRIES, joinPhone, splitPhone } from '../../utils/countries';
import { ApiService } from '../../utils/api';
import { Modal, PrimaryButton } from './TicketingShell';
import { usePreviewMode } from '../../utils/ticketingPreview';
import { useDocumentLimit } from '../../utils/documentLimit';

const inputClass = (error) => `h-12 w-full rounded-xl border bg-white px-4 text-base outline-none transition focus:border-[var(--tk-primary)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--tk-primary)_20%,transparent)] ${error ? 'border-red-400' : 'border-gray-300'}`;

export const FieldShell = ({ field, error, children, wide }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    {field.type !== 'consent' && (
      <label htmlFor={field.id} className="mb-1.5 block text-sm font-medium text-gray-800">
        {field.label}{field.required && <span className="text-red-500"> *</span>}
      </label>
    )}
    {children}
    {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
  </div>
);

export const TextInput = ({ field, value, onChange, error, type = 'text' }) => (
  <input
    id={field.id}
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.placeholder || ''}
    aria-invalid={!!error}
    className={inputClass(error)}
  />
);

const OptionList = ({ id, label, value, onChange, error, placeholder, options, searchable, trigger, menuClass }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const selected = options.find((option) => option.value === value);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((option) => option.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const choose = (option) => {
    onChange(option.value);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, Math.max(shown.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && open && shown[active]) {
      event.preventDefault();
      choose(shown[active]);
    }
  };

  return (
    <div ref={ref} className="relative">
      {trigger ? trigger({ open, toggle: () => { setOpen((v) => !v); setQuery(''); } }) : (
        <button type="button" id={id} aria-haspopup="listbox" aria-expanded={open} aria-invalid={!!error}
          onClick={() => { setOpen((v) => !v); setQuery(''); }} onKeyDown={onKeyDown}
          className={`${inputClass(error)} flex items-center justify-between gap-2 text-left`}>
          <span className={selected ? 'truncate' : 'truncate text-gray-400'}>{selected?.label || placeholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        </button>
      )}
      {open && (
        <div className={`absolute z-30 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg ${menuClass || 'w-full'}`}>
          {searchable && (
            <input autoFocus value={query} onChange={(e) => { setQuery(e.target.value); setActive(0); }} onKeyDown={onKeyDown}
              placeholder="Search…" aria-label={label ? `Search ${label}` : 'Search'}
              className="h-11 w-full border-b border-gray-100 px-4 text-sm outline-none" />
          )}
          <ul role="listbox" aria-label={label} className="max-h-60 overflow-y-auto py-1">
            {shown.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">No matches</li>}
            {shown.map((option, index) => (
              <li key={option.value}>
                <button type="button" role="option" aria-selected={option.value === value}
                  onMouseEnter={() => setActive(index)} onClick={() => choose(option)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm ${index === active ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                  <span className="truncate">{option.label}</span>
                  {option.value === value && <Check className="h-4 w-4 shrink-0 text-[var(--tk-primary)]" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const SelectInput = ({ field, value, onChange, error, options }) => {
  const items = options.map((option) => ({ value: String(option?.value ?? option), label: String(option?.label ?? option) }));
  return (
    <OptionList id={field.id} label={field.label} value={value ?? ''} onChange={onChange} error={error}
      placeholder={field.placeholder || 'Select…'} options={items} searchable={items.length > 8} />
  );
};

const splitChoices = (value) => String(value || '').split(/,\s*/).filter(Boolean);

/** Several choices from a list; stored as "A, B" so exports and the registrations table show it as text. */
export const MultiSelectInput = ({ field, value, onChange, error, options }) => {
  const chosen = splitChoices(value);
  const toggle = (option) => {
    const next = chosen.includes(option) ? chosen.filter((c) => c !== option) : [...chosen, option];
    onChange(options.filter((o) => next.includes(o)).join(', '));
  };
  return (
    <div id={field.id} role="group" aria-label={field.label} aria-invalid={!!error}
      className={`flex flex-wrap gap-2 rounded-xl border bg-white p-3 ${error ? 'border-red-400' : 'border-gray-300'}`}>
      {options.map((option) => {
        const on = chosen.includes(option);
        return (
          <button key={option} type="button" aria-pressed={on} onClick={() => toggle(option)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${on
              ? 'border-[var(--tk-primary)] bg-[var(--tk-primary)] text-white'
              : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}>
            {option}
          </button>
        );
      })}
    </div>
  );
};

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}` }));
const DIAL_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: `${c.flag} ${c.name} ${c.dial}` }));

export const CountrySelect = ({ field, value, onChange, error }) => (
  <OptionList id={field.id} label={field.label} value={value ?? ''} onChange={onChange} error={error}
    placeholder="Select country…" options={COUNTRY_OPTIONS} searchable />
);

export const PhoneInput = ({ field, value, onChange, error, defaultCountry = 'IN' }) => {
  const parsed = useMemo(() => splitPhone(value, defaultCountry), [value, defaultCountry]);
  const [code, setCode] = useState(parsed.code);
  useEffect(() => { if (value) setCode(parsed.code); }, [value, parsed.code]);
  const country = COUNTRIES.find((c) => c.code === code);
  return (
    <div className={`flex rounded-xl border bg-white focus-within:border-[var(--tk-primary)] ${error ? 'border-red-400' : 'border-gray-300'}`}>
      <OptionList id={`${field.id}-code`} label="Country code" value={code} searchable menuClass="w-72"
        options={DIAL_OPTIONS}
        onChange={(next) => { setCode(next); onChange(joinPhone(next, parsed.number)); }}
        trigger={({ open, toggle }) => (
          <button type="button" aria-label="Country code" aria-expanded={open} onClick={toggle}
            className="flex h-12 items-center gap-1 border-r bg-gray-50 px-3 text-sm">
            <span aria-hidden>{country?.flag}</span>
            <span className="tabular-nums">{country?.dial}</span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          </button>
        )} />
      <input
        id={field.id}
        type="tel"
        inputMode="tel"
        value={parsed.number}
        onChange={(e) => onChange(joinPhone(code, e.target.value.replace(/[^\d\s-]/g, '')))}
        placeholder={field.placeholder || 'Mobile number'}
        aria-invalid={!!error}
        className="h-12 min-w-0 flex-1 px-4 text-base outline-none"
      />
    </div>
  );
};

/** Consent checkbox; the text links to the event's terms and privacy pages. */
export const ConsentInput = ({ field, value, onChange, error, termsUrl, privacyUrl, consentText }) => (
  <label htmlFor={field.id} className={`flex gap-3 rounded-xl border p-4 text-sm leading-relaxed ${error ? 'border-red-400' : 'border-gray-200'}`}>
    <input id={field.id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[var(--tk-primary)]" />
    <span className="text-gray-700">
      {field.placeholder || consentText || field.label}
      {(termsUrl || privacyUrl) && ' '}
      {termsUrl && <a href={termsUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Terms &amp; Conditions</a>}
      {termsUrl && privacyUrl && ' and '}
      {privacyUrl && <a href={privacyUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Privacy Policy</a>}
      {(termsUrl || privacyUrl) && '.'}
      {field.required && <span className="text-red-500"> *</span>}
    </span>
  </label>
);

/** Uploads straight away; the value is the stored document id. */
export const FileInput = ({ field, value, onChange, error, eventId, uploadUserId, onUploaded }) => {
  const preview = usePreviewMode();
  const documentLimit = useDocumentLimit();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState(null);
  const pick = async (file) => {
    if (!file) return;
    if (file.size > documentLimit.bytes) { setProblem(`Files can be at most ${documentLimit.mb} MB`); return; }
    if (preview) {
      setName(file.name);
      onChange(`preview:${file.name}`);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const result = await ApiService.uploadFile(file, eventId, field.label.toUpperCase(), uploadUserId);
      setName(file.name);
      onUploaded?.(result.userId);
      onChange(result.documentId || result.externalId);
    } catch (_) {
      setProblem('Upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`flex items-center gap-3 rounded-xl border border-dashed p-4 ${error ? 'border-red-400' : 'border-gray-300'} bg-gray-50`}>
      <FileUp className="h-6 w-6 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1 text-sm">
        {value ? (
          <span className="flex items-center gap-1 font-medium text-green-700"><CheckCircle2 className="h-4 w-4" />{name || 'Uploaded'}</span>
        ) : (
          <span className="text-gray-600">{field.placeholder || `PDF, JPG or PNG up to ${documentLimit.mb} MB`}</span>
        )}
        {problem && <span className="block text-red-600">{problem}</span>}
      </div>
      <label className="cursor-pointer rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : value ? 'Replace' : 'Choose file'}
        <input type="file" accept={field.accept || 'image/*,application/pdf'} className="sr-only" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} />
      </label>
    </div>
  );
};

const cropToBlob = (image, view, size) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, size.width, size.height);
  const ratio = size.width / view.frame;
  ctx.drawImage(image, view.x * ratio, view.y * ratio, image.naturalWidth * view.scale * ratio, image.naturalHeight * view.scale * ratio);
  canvas.toBlob(resolve, 'image/jpeg', 0.9);
});

/** Square-ish crop with drag to move and a zoom slider. */
const Cropper = ({ src, aspect, onCancel, onDone }) => {
  const frame = 280;
  const frameH = Math.round(frame / aspect);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [view, setView] = useState(null);
  const [zoom, setZoom] = useState(1);

  const onLoad = () => {
    const img = imgRef.current;
    const base = Math.max(frame / img.naturalWidth, frameH / img.naturalHeight);
    setView({ base, scale: base, frame, x: (frame - img.naturalWidth * base) / 2, y: (frameH - img.naturalHeight * base) / 2 });
  };
  const clamp = (v) => {
    const img = imgRef.current;
    const w = img.naturalWidth * v.scale;
    const h = img.naturalHeight * v.scale;
    return { ...v, x: Math.min(0, Math.max(frame - w, v.x)), y: Math.min(0, Math.max(frameH - h, v.y)) };
  };
  const setZoomLevel = (z) => {
    setZoom(z);
    setView((v) => {
      const scale = v.base * z;
      const cx = frame / 2 - ((frame / 2 - v.x) / v.scale) * scale;
      const cy = frameH / 2 - ((frameH / 2 - v.y) / v.scale) * scale;
      return clamp({ ...v, scale, x: cx, y: cy });
    });
  };
  const pointer = {
    onPointerDown: (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { x: e.clientX, y: e.clientY }; },
    onPointerMove: (e) => {
      if (!drag.current || !view) return;
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      drag.current = { x: e.clientX, y: e.clientY };
      setView((v) => clamp({ ...v, x: v.x + dx, y: v.y + dy }));
    },
    onPointerUp: () => { drag.current = null; },
  };
  return (
    <div className="space-y-4">
      <div className="mx-auto touch-none overflow-hidden rounded-xl bg-gray-900" style={{ width: frame, height: frameH }} {...pointer}>
        <img
          ref={imgRef}
          src={src}
          alt="Photo to crop"
          onLoad={onLoad}
          draggable={false}
          className="max-w-none select-none"
          style={view ? { transform: `translate(${view.x}px, ${view.y}px)`, width: imgRef.current.naturalWidth * view.scale, transformOrigin: '0 0' } : { opacity: 0 }}
        />
      </div>
      <label className="flex items-center gap-3 text-sm text-gray-600">
        Zoom
        <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoomLevel(Number(e.target.value))} className="flex-1 accent-[var(--tk-primary)]" disabled={!view} />
      </label>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rounded-xl border px-4 py-2.5 font-medium">Cancel</button>
        <PrimaryButton disabled={!view} onClick={() => onDone(imgRef.current, { ...view, frame })}>Crop</PrimaryButton>
      </div>
    </div>
  );
};

const CameraCapture = ({ onCancel, onShot }) => {
  const videoRef = useRef(null);
  const [problem, setProblem] = useState(null);
  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((s) => { stream = s; if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); } })
      .catch(() => setProblem('Camera is not available. Allow camera access or choose a file instead.'));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, []);
  const shoot = () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    onShot(canvas.toDataURL('image/jpeg', 0.92));
  };
  return (
    <div className="space-y-4">
      {problem ? <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{problem}</p>
        : <video ref={videoRef} playsInline muted className="w-full rounded-xl bg-gray-900" />}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rounded-xl border px-4 py-2.5 font-medium">Cancel</button>
        {!problem && <PrimaryButton onClick={shoot}><Camera className="h-4 w-4" />Capture</PrimaryButton>}
      </div>
    </div>
  );
};

/**
 * Profile photo: pick or take a picture, crop it to the configured size (validation.width/height, default 250×250)
 * and upload. The value is the stored document id; the preview stays local.
 */
const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

/** Server-side face check; null when it can't run, so a photo is never blocked by an outage. */
const checkFace = async (eventId, blob) => {
  try {
    const result = await ApiService.checkPhoto(eventId, await blobToDataUrl(blob));
    return result?.checked ? result : null;
  } catch (_) {
    return null;
  }
};

export const PhotoInput = ({ field, value, onChange, error, eventId, uploadUserId, onUploaded }) => {
  const size = { width: Number(field.validation?.width) || 250, height: Number(field.validation?.height) || 250 };
  const previewMode = usePreviewMode();
  const [stage, setStage] = useState(null);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [faceProblems, setFaceProblems] = useState([]);
  const [faceOk, setFaceOk] = useState(false);

  const choose = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setProblem('Choose a JPG or PNG image'); return; }
    const reader = new FileReader();
    reader.onload = () => { setSource(reader.result); setStage('crop'); };
    reader.readAsDataURL(file);
  };
  const crop = async (image, view) => {
    setBusy(true);
    setProblem(null);
    try {
      const blob = await cropToBlob(image, view, size);
      const verdict = await checkFace(eventId, blob);
      if (verdict && !verdict.ok) {
        setPreview(URL.createObjectURL(blob));
        setFaceProblems(verdict.problems || []);
        setFaceOk(false);
        setStage(null);
        return;
      }
      setFaceProblems([]);
      setFaceOk(!!verdict);
      if (previewMode) {
        setPreview(URL.createObjectURL(blob));
        onChange('preview:photo');
        setStage(null);
        return;
      }
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      const result = await ApiService.uploadFile(file, eventId, field.label.toUpperCase(), uploadUserId);
      onUploaded?.(result.userId);
      setPreview(URL.createObjectURL(blob));
      onChange(result.documentId || result.externalId);
      setStage(null);
    } catch (_) {
      setProblem('Upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className={`flex flex-wrap items-center gap-4 rounded-xl border border-dashed bg-gray-50 p-4 ${error ? 'border-red-400' : 'border-gray-300'}`}>
        {preview ? <img src={preview} alt="Your photo" className={`h-20 w-20 rounded-lg object-cover ${faceProblems.length ? 'ring-2 ring-red-400' : ''}`} />
          : <span className="flex h-20 w-20 items-center justify-center rounded-lg bg-white text-gray-300 ring-1 ring-gray-200"><ImagePlus className="h-8 w-8" /></span>}
        <div className="min-w-0 flex-1 text-sm text-gray-600">
          {faceProblems.length > 0 ? (
            <div role="alert" className="text-red-700">
              <span className="flex items-center gap-1 font-medium"><X className="h-4 w-4" />This photo can't be used</span>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">{faceProblems.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          ) : value ? (
            <span className="flex items-center gap-1 font-medium text-green-700">
              <CheckCircle2 className="h-4 w-4" />{faceOk ? 'Face detected' : 'Photo added'}
            </span>
          ) : <span>{field.placeholder || `A clear photo of your face. It will be cropped to ${size.width}×${size.height}.`}</span>}
          {problem && <span className="block text-red-600">{problem}</span>}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setStage('camera')} className="flex items-center gap-1 rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100">
            <Camera className="h-4 w-4" />Take photo
          </button>
          <label className="flex cursor-pointer items-center gap-1 rounded-lg border bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100">
            {value ? <RotateCcw className="h-4 w-4" /> : <FileUp className="h-4 w-4" />}{value ? 'Change' : 'Choose file'}
            <input type="file" accept="image/png,image/jpeg" className="sr-only" onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>
      </div>
      <Modal open={stage === 'camera'} onClose={() => setStage(null)} title="Take a photo">
        <CameraCapture onCancel={() => setStage(null)} onShot={(data) => { setSource(data); setStage('crop'); }} />
      </Modal>
      <Modal open={stage === 'crop'} onClose={() => !busy && setStage(null)} title="Crop your photo">
        {busy ? (
          <div className="flex flex-col items-center gap-3 py-12 text-sm text-gray-600">
            <Loader2 className="h-8 w-8 animate-spin" />Checking your photo…
          </div>
        )
          : source && <Cropper src={source} aspect={size.width / size.height} onCancel={() => setStage(null)} onDone={crop} />}
      </Modal>
    </div>
  );
};

/** Email with a Verify button; a 6-digit code makes it verified for this booking. */
export const VerifiedEmailInput = ({ field, value, onChange, error, eventId, verified, onVerified }) => {
  const preview = usePreviewMode();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const boxes = useRef([]);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const sent = preview ? { resendAfterSeconds: 30 } : await ApiService.sendEmailCode(eventId, value);
      setWait(sent?.resendAfterSeconds || 30);
      setCode(['', '', '', '', '', '']);
      setOpen(true);
      setTimeout(() => boxes.current[0]?.focus(), 50);
    } catch (err) {
      setProblem(err.message || 'Could not send the code');
    } finally {
      setBusy(false);
    }
  };
  const verify = async (digits = code) => {
    setBusy(true);
    setProblem(null);
    try {
      const result = preview ? { token: 'preview' } : await ApiService.verifyEmailCode(eventId, value, digits.join(''));
      onVerified(result.token);
      setOpen(false);
    } catch (err) {
      setProblem(err.message || 'That code is not right');
    } finally {
      setBusy(false);
    }
  };
  const type = (i, char) => {
    const digits = [...code];
    const clean = char.replace(/\D/g, '');
    if (clean.length > 1) {
      clean.slice(0, 6).split('').forEach((d, j) => { if (i + j < 6) digits[i + j] = d; });
    } else {
      digits[i] = clean;
    }
    setCode(digits);
    const next = digits.findIndex((d) => !d);
    if (next >= 0 && clean) boxes.current[Math.min(next, 5)]?.focus();
    if (digits.every(Boolean)) verify(digits);
  };
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

  return (
    <>
      <div className={`flex overflow-hidden rounded-xl border bg-white focus-within:border-[var(--tk-primary)] ${error || problem ? 'border-red-400' : 'border-gray-300'}`}>
        <input
          id={field.id}
          type="email"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || 'you@example.com'}
          className="h-12 min-w-0 flex-1 px-4 text-base outline-none"
          aria-invalid={!!error}
        />
        {verified ? (
          <span className="flex items-center gap-1 px-4 text-sm font-semibold text-green-700"><ShieldCheck className="h-4 w-4" />Verified</span>
        ) : (
          <button type="button" onClick={send} disabled={!looksValid || busy} className="m-1.5 rounded-lg bg-[var(--tk-primary)] px-4 text-sm font-semibold text-white disabled:opacity-40">
            {busy && !open ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
          </button>
        )}
      </div>
      {problem && !open && <p className="mt-1 text-sm text-red-600">{problem}</p>}
      <Modal open={open} onClose={() => setOpen(false)} title="Enter verification code">
        <p className="text-sm text-gray-600">Enter the 6-digit code sent to <b>{value}</b>.</p>
        {preview && <p className="mt-2 rounded-lg bg-blue-50 p-2 text-xs text-blue-800">Preview: no email is sent. Any 6 digits will verify.</p>}
        <div className="mt-5 flex justify-center gap-2">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { boxes.current[i] = el; }}
              value={digit}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`Digit ${i + 1}`}
              onChange={(e) => type(i, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Backspace' && !digit && i > 0) boxes.current[i - 1]?.focus(); }}
              className="h-12 w-11 rounded-xl border border-gray-300 text-center text-xl font-semibold outline-none focus:border-[var(--tk-primary)]"
            />
          ))}
        </div>
        {problem && <p className="mt-3 text-center text-sm text-red-600">{problem}</p>}
        <PrimaryButton className="mt-5 w-full" disabled={busy || code.some((d) => !d)} onClick={() => verify()}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify code'}
        </PrimaryButton>
        <div className="mt-3 text-center text-sm">
          {wait > 0 ? <span className="text-gray-500">Resend code in {wait}s</span>
            : <button type="button" onClick={send} disabled={busy} className="font-medium text-[var(--tk-primary)] underline">Resend code</button>}
        </div>
        <p className="mt-4 text-center text-xs text-gray-500">Not in your inbox? Check spam or promotions. The code is valid for 5 minutes.</p>
      </Modal>
    </>
  );
};

export const CloseIcon = X;
